import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  createContext as createSelectContext,
  useContext,
} from 'use-context-selector';

import { DEFAULT_COUNTRY_DATA } from '~common';
import { allCountries, CountryItem } from '~configs';
import { useQueryGetVenueCountryCode } from '~hooks/station/useQueryGetVenueCountryCode';

type VenueCountryContextType = {
  defaultCountry: CountryItem;
  isLoadingVenueCountry: boolean;
};

const VenueCountryContext = createSelectContext<VenueCountryContextType>({
  defaultCountry: DEFAULT_COUNTRY_DATA,
  isLoadingVenueCountry: false,
});

type Props = {
  children: React.ReactNode;
};

export const VenueCountryProvider: React.FC<Props> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const [defaultCountry, setDefaultCountry] =
    useState<CountryItem>(DEFAULT_COUNTRY_DATA);

  const stationSerialNumber = searchParams.get('box');
  const venueCountryQuery = useQueryGetVenueCountryCode(
    stationSerialNumber ?? '',
    {
      onSuccess(data) {
        const code = data?.countryCode;
        if (!code) return;

        const iso2 = code.toLowerCase();
        const found = allCountries.find((c) => c.iso2.toLowerCase() === iso2);
        const next = found || DEFAULT_COUNTRY_DATA;

        setDefaultCountry(next);
      },
    },
  );

  return (
    <VenueCountryContext.Provider
      value={{
        defaultCountry,
        isLoadingVenueCountry: venueCountryQuery.isFetching,
      }}
    >
      {children}
    </VenueCountryContext.Provider>
  );
};

export const useVenueCountryContext = () => useContext(VenueCountryContext);
